/*
 * WhatsApp — Admin console for Meta Cloud API integration.
 *
 * Three tabs:
 *   1. Overview – configuration status + KPI counts (sent / delivered
 *      / read / failed / skipped).
 *   2. Message Log – every attempt with recipient, template, status.
 *   3. Contacts & Opt-in – bulk CSV importer + per-contact toggle.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../App';
import { userCan } from '../utils/permissionUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  MessageSquare, CheckCircle2, XCircle, Clock, RefreshCw, Upload,
  Users as UsersIcon, AlertTriangle, BookOpen, Eye,
} from 'lucide-react';

const STATUS_COLORS = {
  sent: 'bg-blue-100 text-blue-800 border-blue-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  read: 'bg-green-100 text-green-800 border-green-200',
  failed: 'bg-rose-100 text-rose-800 border-rose-200',
  skipped: 'bg-slate-100 text-slate-600 border-slate-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
};

/** Minimal, safe Markdown → HTML renderer for our internal setup guide.
 *  Supports headings (# ## ###), bold, italic, inline code, code blocks,
 *  bullet + numbered lists, links, and horizontal rules. Escapes HTML
 *  to keep it safe even though the source is a trusted repo file. */
function renderMarkdown(md) {
  if (!md) return '';
  const escapeHtml = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = md.split('\n');
  const out = [];
  let inCode = false;
  let inList = null;   // 'ul' | 'ol' | null
  const closeList = () => {
    if (inList) { out.push(`</${inList}>`); inList = null; }
  };
  const inline = (s) => escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-[12px] font-mono text-rose-700">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-emerald-700 underline">$1</a>');

  for (const raw of lines) {
    const line = raw;
    if (/^```/.test(line)) {
      if (inCode) { out.push('</code></pre>'); inCode = false; }
      else { closeList(); out.push('<pre class="bg-slate-900 text-slate-100 rounded p-3 my-2 text-[12px] overflow-x-auto"><code>'); inCode = true; }
      continue;
    }
    if (inCode) { out.push(escapeHtml(line)); continue; }
    if (/^\s*---\s*$/.test(line)) { closeList(); out.push('<hr class="my-4 border-slate-200"/>'); continue; }
    let m;
    if ((m = line.match(/^### (.*)$/))) { closeList(); out.push(`<h3 class="text-base font-semibold text-slate-900 mt-5 mb-1">${inline(m[1])}</h3>`); continue; }
    if ((m = line.match(/^## (.*)$/))) { closeList(); out.push(`<h2 class="text-lg font-bold text-slate-900 mt-6 mb-2">${inline(m[1])}</h2>`); continue; }
    if ((m = line.match(/^# (.*)$/))) { closeList(); out.push(`<h1 class="text-xl font-bold text-slate-900 mt-6 mb-3">${inline(m[1])}</h1>`); continue; }
    if ((m = line.match(/^\s*(\d+)\.\s+(.*)$/))) {
      if (inList !== 'ol') { closeList(); out.push('<ol class="list-decimal ml-6 space-y-1 my-2">'); inList = 'ol'; }
      out.push(`<li>${inline(m[2])}</li>`);
      continue;
    }
    if ((m = line.match(/^\s*[-*]\s+(.*)$/))) {
      if (inList !== 'ul') { closeList(); out.push('<ul class="list-disc ml-6 space-y-1 my-2">'); inList = 'ul'; }
      out.push(`<li>${inline(m[1])}</li>`);
      continue;
    }
    if (/^\s*$/.test(line)) { closeList(); out.push(''); continue; }
    closeList();
    out.push(`<p class="my-1.5 text-slate-700 leading-relaxed">${inline(line)}</p>`);
  }
  closeList();
  if (inCode) out.push('</code></pre>');
  return out.join('\n');
}

export default function WhatsApp({ user }) {
  const [tab, setTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState({ rows: [], counts: {} });
  const [logDetail, setLogDetail] = useState(null);
  const [msgPreview, setMsgPreview] = useState({ open: false, loading: false, data: null });
  const [contacts, setContacts] = useState({ rows: [], total: 0 });
  const [templates, setTemplates] = useState(null);
  const [syncing, setSyncing] = useState(null); // 'sims' | 'riders' | null
  const [syncResult, setSyncResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditSummary, setAuditSummary] = useState(null);
  const [mirrorState, setMirrorState] = useState({ previewing: false, applying: false, open: false, preview: null });
  const [loading, setLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({ status: '', q: '', entity_type: '' });
  const [contactFilter, setContactFilter] = useState({ q: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideMd, setGuideMd] = useState('');
  const [guideLoading, setGuideLoading] = useState(false);

  const openMessagePreview = async (logRow) => {
    setMsgPreview({ open: true, loading: true, data: null });
    try {
      const res = await axios.get(
        `${API}/whatsapp/logs/${logRow.id}/preview`,
        { headers: getAuthHeader() },
      );
      setMsgPreview({ open: true, loading: false, data: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load message preview');
      setMsgPreview({ open: false, loading: false, data: null });
    }
  };

  const openGuide = async () => {
    setGuideOpen(true);
    if (guideMd) return;
    try {
      setGuideLoading(true);
      const res = await axios.get(`${API}/whatsapp/setup-guide`, { headers: getAuthHeader() });
      setGuideMd(res.data.markdown || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load setup guide');
    } finally {
      setGuideLoading(false);
    }
  };

  const isAdmin = userCan(user, 'adminTools', 'view', 'whatsapp');

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/status`, { headers: getAuthHeader() });
      setStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load status');
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (logFilter.status) params.status = logFilter.status;
      if (logFilter.q) params.q = logFilter.q;
      if (logFilter.entity_type) params.entity_type = logFilter.entity_type;
      const res = await axios.get(`${API}/whatsapp/logs`, { params, headers: getAuthHeader() });
      setLogs(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (contactFilter.q) params.q = contactFilter.q;
      const res = await axios.get(`${API}/whatsapp/contacts`, { params, headers: getAuthHeader() });
      setContacts(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/whatsapp/templates/catalog`, { headers: getAuthHeader() });
      setTemplates(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load template catalog');
    } finally {
      setLoading(false);
    }
  };

  const syncFromSims = async () => {
    try {
      setSyncing('sims');
      setSyncResult(null);
      const res = await axios.post(`${API}/whatsapp/optin/sync-sims`, {}, { headers: getAuthHeader() });
      setSyncResult({ label: 'SIM inventory sync', ...res.data });
      toast.success(`SIM sync complete — ${res.data.added} added, ${res.data.reactivated} re-added, ${res.data.already_opted_in} already opted-in`);
      await fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SIM sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const syncFromRiders = async () => {
    try {
      setSyncing('riders');
      setSyncResult(null);
      const res = await axios.post(`${API}/whatsapp/optin/sync-riders`, {}, { headers: getAuthHeader() });
      setSyncResult({ label: 'Riders sync', ...res.data });
      toast.success(`Riders sync complete — ${res.data.added} added, ${res.data.reactivated} re-added, ${res.data.already_opted_in} already opted-in`);
      await fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Riders sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const syncAllPhones = async () => {
    try {
      setSyncing('all');
      setSyncResult(null);
      const res = await axios.post(`${API}/whatsapp/optin/sync-all`, {}, { headers: getAuthHeader() });
      const t = res.data?.totals || {};
      setSyncResult({ label: 'All modules sync', ...t, per_collection: res.data?.per_collection });
      toast.success(`All phones synced — ${t.added || 0} added, ${t.reactivated || 0} re-added, ${t.already_opted_in || 0} already opted-in`);
      await fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Full sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const runPhoneAudit = async () => {
    try {
      setAuditing(true);
      setAuditSummary(null);
      // 1) Fetch JSON summary
      const jsonRes = await axios.get(`${API}/whatsapp/audit/rider-sim-phone-mismatch`, { headers: getAuthHeader() });
      setAuditSummary(jsonRes.data.summary);
      // 2) Fetch CSV and download
      const csvRes = await axios.get(`${API}/whatsapp/audit/rider-sim-phone-mismatch?format=csv`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([csvRes.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rider_sim_phone_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Audit downloaded — ${jsonRes.data.summary.mismatch} mismatches found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Phone audit failed');
    } finally {
      setAuditing(false);
    }
  };

  // Mirror SIM inventory → rider mobile_uae. Two-step: preview (dry_run) then apply.
  const openMirrorPreview = async () => {
    try {
      setMirrorState((s) => ({ ...s, previewing: true }));
      const res = await axios.post(`${API}/whatsapp/audit/apply-sim-to-rider?dry_run=true`, {}, { headers: getAuthHeader() });
      setMirrorState({ previewing: false, applying: false, open: true, preview: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mirror preview failed');
      setMirrorState({ previewing: false, applying: false, open: false, preview: null });
    }
  };

  const applyMirror = async () => {
    try {
      setMirrorState((s) => ({ ...s, applying: true }));
      const res = await axios.post(`${API}/whatsapp/audit/apply-sim-to-rider`, {}, { headers: getAuthHeader() });
      toast.success(`Mirror applied — ${res.data.updated} rider mobiles updated`);
      setMirrorState({ previewing: false, applying: false, open: false, preview: null });
      await fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mirror apply failed');
      setMirrorState((s) => ({ ...s, applying: false }));
    }
  };

  const downloadOrphansCsv = () => {
    const orphans = mirrorState.preview?.orphan_sims || [];
    if (!orphans.length) return;
    const header = ['sim_rider_unique_id', 'sim_rider_name', 'sim_mobile', 'sim_platform', 'sim_monthly_plan', 'sim_service_provider', 'sim_date_of_handover', 'sim_rider_id_internal'];
    const rows = orphans.map(o => [
      o.sim_rider_unique_id || '',
      (o.sim_rider_name || '').replace(/"/g, '""'),
      o.sim_mobile || '',
      o.sim_platform || '',
      o.sim_monthly_plan || '',
      o.sim_service_provider || '',
      o.sim_date_of_handover || '',
      o.sim_rider_id || '',
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orphan_sims_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${orphans.length} orphan SIM rows`);
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchStatus();
    fetchLogs();
  }, [isAdmin]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs();
    if (tab === 'contacts') fetchContacts();
    if (tab === 'templates') fetchTemplates();
  }, [tab]);

  const uploadCsv = async () => {
    if (!csvFile) {
      toast.error('Please choose a CSV file first');
      return;
    }
    try {
      setImporting(true);
      const fd = new FormData();
      fd.append('file', csvFile);
      fd.append('default_opted_in', 'true');
      const res = await axios.post(`${API}/whatsapp/optin/import`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported: ${res.data.inserted} new, ${res.data.updated} updated, ${res.data.invalid} invalid`);
      setCsvFile(null);
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const toggleContact = async (contact, next) => {
    try {
      await axios.post(`${API}/whatsapp/optin/set`, {
        phone: contact.e164,
        opted_in: next,
        name: contact.name,
        type: contact.type,
        entity_id: contact.entity_id,
      }, { headers: getAuthHeader() });
      toast.success(next ? 'Opted-in' : 'Opted-out');
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            You don't have permission to open the WhatsApp console. Ask an admin to tick Admin Tools → WhatsApp Console.
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = logs.counts || {};

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="whatsapp-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-bold text-slate-900">WhatsApp</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openGuide} data-testid="whatsapp-guide-btn" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <BookOpen className="w-4 h-4 mr-1.5" /> Setup Guide
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchLogs(); }} data-testid="whatsapp-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {status && status.business_phone_number && (
        <Card className="border-slate-200">
          <CardContent className="p-3 flex items-center gap-3 text-sm">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Business sender number</div>
              <div className="font-mono text-slate-900 font-semibold">{status.business_phone_number}</div>
            </div>
            <div className="ml-auto text-[10px] text-slate-500 max-w-md text-right">
              This number must be registered as your WhatsApp Business phone via Meta Business Manager. Once registered, Meta issues a numeric <b>Phone Number ID</b> which goes into <code>WHATSAPP_PHONE_NUMBER_ID</code>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Config banner */}
      {status && !status.configured && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">WhatsApp integration not yet configured.</div>
              <div className="text-amber-800 mt-1">
                Set the following environment variables in <code className="bg-white px-1 rounded">backend/.env</code>:
                {['WHATSAPP_ACCESS_TOKEN','WHATSAPP_PHONE_NUMBER_ID','WHATSAPP_APP_SECRET','WHATSAPP_VERIFY_TOKEN'].map(k => (
                  <span key={k} className={`inline-block px-2 py-0.5 rounded font-mono text-xs mr-1 mt-1 ${status[`has_${k.replace('WHATSAPP_','').toLowerCase()}`] ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{k}</span>
                ))}
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 border-emerald-600 text-emerald-800" onClick={openGuide} data-testid="banner-guide-btn">
                    <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Open Setup Guide
                  </Button>
                  <span className="text-xs text-amber-800">— step-by-step instructions with screenshots.</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {status && status.configured && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-emerald-900">
            <CheckCircle2 className="w-4 h-4" /> WhatsApp integration is configured and ready.
            <span className="ml-auto text-xs text-emerald-700">Graph API {status.graph_version}</span>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Message Log</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts &amp; Opt-in</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { key: 'total', label: 'Total', color: 'bg-slate-50', icon: MessageSquare },
              { key: 'sent', label: 'Sent', color: 'bg-blue-50', icon: MessageSquare },
              { key: 'delivered', label: 'Delivered', color: 'bg-emerald-50', icon: CheckCircle2 },
              { key: 'read', label: 'Read', color: 'bg-green-50', icon: CheckCircle2 },
              { key: 'failed', label: 'Failed', color: 'bg-rose-50', icon: XCircle },
              { key: 'skipped', label: 'Skipped', color: 'bg-amber-50', icon: Clock },
            ].map(item => (
              <Card key={item.key} className={item.color}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-slate-600 text-xs">
                    <item.icon className="w-3.5 h-3.5" /> {item.label}
                  </div>
                  <div className="text-2xl font-bold mt-1">{c[item.key] ?? 0}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What&apos;s built (Phase A) &amp; What&apos;s next (Phase B)</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-2">
              <div><b>✅ Phase A (built):</b> Meta Cloud API client, UAE phone normalizer, webhook receiver with signature validation, message audit log, bulk contact opt-in importer.</div>
              <div><b>⏳ Phase B (post-credentials):</b> Auto-send on fine creation, manual &quot;Send via WhatsApp&quot; buttons on Warnings + Handovers, template selection UI.</div>
              <div className="text-xs text-slate-500 mt-3 border-t pt-2">
                Before Phase B can activate you need to: (1) complete Meta business verification, (2) submit 3 templates for approval, (3) paste credentials into <code>backend/.env</code>. See <code>WHATSAPP_SETUP_GUIDE.md</code>.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4" data-testid="templates-tab-content">
          {templates ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-slate-50">
                  <CardContent className="p-3">
                    <div className="text-slate-600 text-xs">Total templates</div>
                    <div className="text-2xl font-bold mt-1" data-testid="tpl-total">{templates.count}</div>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="p-3">
                    <div className="text-emerald-700 text-xs">Automatic</div>
                    <div className="text-2xl font-bold mt-1 text-emerald-800" data-testid="tpl-auto">{templates.auto_count}</div>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-50">
                  <CardContent className="p-3">
                    <div className="text-indigo-700 text-xs">Manual (admin-triggered)</div>
                    <div className="text-2xl font-bold mt-1 text-indigo-800" data-testid="tpl-manual">{templates.manual_count}</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50">
                  <CardContent className="p-3">
                    <div className="text-amber-700 text-xs">Wired in code today</div>
                    <div className="text-2xl font-bold mt-1 text-amber-800" data-testid="tpl-wired">{templates.wired_count} / {templates.count}</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Template Catalog</CardTitle>
                  <div className="text-xs text-slate-500 mt-1">
                    Source-of-truth for which templates fire automatically vs which are triggered manually by admin.
                    <span className="ml-1">
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">AUTO</Badge>
                      &nbsp;fires on a system event —
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 ml-1">MANUAL</Badge>
                      &nbsp;requires an admin click.
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Template</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Fires when</TableHead>
                        <TableHead>Body params</TableHead>
                        <TableHead>Media</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.templates.map((t, i) => (
                        <TableRow key={t.name} data-testid={`tpl-row-${t.name}`}>
                          <TableCell className="text-slate-500">{i + 1}</TableCell>
                          <TableCell>
                            <div className="font-mono text-[13px] font-semibold text-slate-900">{t.name}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{t.category}</div>
                          </TableCell>
                          <TableCell>
                            {t.trigger === 'auto' ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">AUTO</Badge>
                            ) : (
                              <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">MANUAL</Badge>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs text-[12.5px] text-slate-700 leading-snug">
                            {t.fires_when}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(t.params || []).map((p, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center rounded bg-slate-100 text-slate-700 text-[10.5px] font-mono px-1.5 py-0.5"
                                >
                                  {`{{${idx + 1}}}`}&nbsp;{p}
                                </span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {t.media_type ? (
                              <div className="flex flex-col gap-1">
                                <Badge className="bg-slate-100 text-slate-800 border-slate-200 w-fit uppercase text-[10px]">
                                  {t.media_type}
                                </Badge>
                                {t.sample_media_url && (
                                  <a
                                    href={`${API}${t.sample_media_url.replace(/^\/api/, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] text-emerald-700 underline"
                                    data-testid={`tpl-sample-${t.name}`}
                                  >
                                    Download sample PDF
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400">None</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {t.wired ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">Wired</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending wiring</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-sm text-slate-500 py-8 text-center">Loading catalog…</div>
          )}
        </TabsContent>

        <TabsContent value="logs" className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">Search</Label>
              <Input
                value={logFilter.q}
                onChange={e => setLogFilter(v => ({ ...v, q: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && fetchLogs()}
                placeholder="Name / phone / template"
                className="h-9 w-64"
                data-testid="log-search"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={logFilter.status}
                onChange={e => setLogFilter(v => ({ ...v, status: e.target.value }))}
                className="h-9 border rounded px-2 text-sm"
                data-testid="log-status-filter"
              >
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Entity Type</Label>
              <select
                value={logFilter.entity_type}
                onChange={e => setLogFilter(v => ({ ...v, entity_type: e.target.value }))}
                className="h-9 border rounded px-2 text-sm"
                data-testid="log-entity-filter"
              >
                <option value="">All</option>
                <option value="fine">Fine</option>
                <option value="bike_handover">Bike Handover</option>
                <option value="bike_takeover">Bike Takeover</option>
                <option value="bike_reg_expiry">Bike Reg. Expiry</option>
                <option value="bike_reg_updated">Bike Reg. Updated</option>
                <option value="sim_handover">SIM Handover</option>
                <option value="sim_takeover">SIM Takeover</option>
                <option value="platform_change">Platform Change</option>
                <option value="maintenance">Maintenance</option>
                <option value="salik">Salik</option>
                <option value="salary_slip">Salary Slip</option>
                <option value="hiring_welcome">Hiring Welcome</option>
                <option value="hiring_document">Hiring Document</option>
                <option value="loan_statement">Loan Statement</option>
                <option value="warning_letter">Warning Letter</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <Button size="sm" onClick={fetchLogs} disabled={loading}>Apply</Button>
            <Button
              size="sm"
              variant="outline"
              className="border-rose-300 text-rose-600 hover:bg-rose-50"
              disabled={loading || !(logs.counts?.failed)}
              data-testid="whatsapp-retry-failed-btn"
              onClick={async () => {
                if (!window.confirm(
                  'Retry ALL failed WhatsApp messages?\n\n' +
                  'Media-bearing rows will be skipped. This may take a minute.',
                )) return;
                try {
                  const r = await axios.post(
                    `${API}/whatsapp/messages/retry-failed`,
                    null,
                    { headers: getAuthHeader() },
                  );
                  const d = r.data || {};
                  toast.success(
                    `Retried ${d.retried} — ${d.sent} sent, ${d.still_failed} still failed` +
                    (d.skipped_media ? `, ${d.skipped_media} media skipped` : ''),
                    { duration: 8000 },
                  );
                  fetchLogs();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Retry failed');
                }
              }}
            >
              Retry failed{logs.counts?.failed ? ` (${logs.counts.failed})` : ''}
            </Button>
          </div>
          <div className="overflow-x-auto border rounded">
            <Table>
              <TableHeader className="bg-slate-100">
                <TableRow>
                  <TableHead className="text-xs">When</TableHead>
                  <TableHead className="text-xs">Recipient</TableHead>
                  <TableHead className="text-xs">Phone</TableHead>
                  <TableHead className="text-xs">Template</TableHead>
                  <TableHead className="text-xs">Trigger</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Detail</TableHead>
                  <TableHead className="text-xs text-center">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-400 text-sm">
                      No messages yet. Once Phase B is activated, sent messages will show up here.
                    </TableCell>
                  </TableRow>
                ) : logs.rows.map(r => (
                  <TableRow key={r.id} data-testid={`log-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{r.created_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.recipient_name || '—'}</div>
                      <div className="text-[10px] text-slate-500">{r.recipient_type}</div>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{r.to_e164 || r.to_raw}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-mono">{r.template_name}</div>
                      {r.entity_type && <div className="text-[10px] text-slate-500">{r.entity_type}</div>}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">{r.trigger || '—'}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge className={`${STATUS_COLORS[r.status] || STATUS_COLORS.pending} text-[10px] border`}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs max-w-[280px]">
                      {r.error ? (
                        <button
                          type="button"
                          onClick={() => setLogDetail(r)}
                          className="text-left underline decoration-dotted decoration-slate-400 hover:decoration-slate-700 truncate block w-full"
                          title="Click to see the full error message and troubleshooting hint"
                          data-testid={`log-error-open-${r.id || r.created_at}`}
                        >
                          {r.error}
                        </button>
                      ) : (
                        r.whatsapp_message_id ? <span className="font-mono text-[10px] text-slate-500">{r.whatsapp_message_id.slice(0, 24)}…</span> : '—'
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 w-6 p-0 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
                        onClick={() => openMessagePreview(r)}
                        title="View exact message that was sent"
                        data-testid={`log-preview-${r.id || r.created_at}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
                <RefreshCw className="w-4 h-4" /> One-click sync from existing data
              </CardTitle>
              <div className="text-xs text-slate-600 mt-1">
                Populate the opt-in registry from data already in the system. Safe to re-run anytime — the sync is idempotent and respects earlier opt-outs.
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                onClick={syncAllPhones}
                disabled={syncing}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="sync-all-btn"
                title="Auto opt-in every phone number across SIMs, Riders, TAP, Hiring, Staff, Lessees, Payroll riders and Warning letters — recommended for a fresh setup"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                {syncing === 'all' ? 'Syncing All…' : 'Sync All Phones (All Modules)'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={syncFromSims}
                disabled={syncing}
                className="border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                data-testid="sync-sims-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                {syncing === 'sims' ? 'Syncing SIMs…' : 'Sync from SIM inventory'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={syncFromRiders}
                disabled={syncing}
                className="border-slate-400 text-slate-800 hover:bg-slate-100"
                data-testid="sync-riders-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                {syncing === 'riders' ? 'Syncing Riders…' : 'Sync from Riders'}
              </Button>
              {syncResult && (
                <div className="text-xs bg-white border border-emerald-300 rounded px-3 py-1.5" data-testid="sync-result">
                  <b className="text-emerald-800">{syncResult.label}:</b>&nbsp;
                  +{syncResult.added || 0} added · {syncResult.reactivated || 0} re-added · {syncResult.already_opted_in || 0} already opted-in · {syncResult.skipped_no_mobile || 0} skipped (no phone)
                </div>
              )}
              <div className="w-full pt-2 mt-1 border-t border-emerald-100 flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-600 flex-1 min-w-[240px]">
                  <b className="text-slate-800">Data-integrity audit —</b> compare each rider&apos;s SIM inventory number against their Rider page personal mobile. Downloads a CSV highlighting mismatches, duplicates, and gaps.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runPhoneAudit}
                  disabled={auditing}
                  className="border-amber-400 text-amber-800 hover:bg-amber-50"
                  data-testid="audit-phone-btn"
                >
                  {auditing ? 'Running audit…' : 'Download Phone Audit CSV'}
                </Button>
                <Button
                  size="sm"
                  onClick={openMirrorPreview}
                  disabled={mirrorState.previewing || mirrorState.applying}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="mirror-sim-btn"
                >
                  {mirrorState.previewing ? 'Loading preview…' : 'Mirror SIM → Rider Mobile'}
                </Button>
                {auditSummary && (
                  <div className="text-[11px] bg-white border border-amber-300 rounded px-3 py-1.5" data-testid="audit-summary">
                    <b className="text-amber-800">Audit:</b>&nbsp;
                    {auditSummary.total_riders} riders · {auditSummary.match} match · <b className="text-red-700">{auditSummary.mismatch} MISMATCH</b> · {auditSummary.no_sim} no-sim · {auditSummary.no_personal} no-personal · <b>{auditSummary.duplicate_sim + auditSummary.duplicate_personal} duplicates</b>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex items-end justify-between gap-2 flex-wrap">
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs">Search contacts</Label>
                <Input
                  value={contactFilter.q}
                  onChange={e => setContactFilter({ q: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && fetchContacts()}
                  placeholder="Name or phone"
                  className="h-9 w-64"
                  data-testid="contact-search"
                />
              </div>
              <Button size="sm" onClick={fetchContacts} disabled={loading}>Apply</Button>
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-1">
              <UsersIcon className="w-4 h-4" /> {contacts.total} contact{contacts.total === 1 ? '' : 's'}
            </div>
          </div>
          <div className="overflow-x-auto border rounded">
            <Table>
              <TableHeader className="bg-slate-100">
                <TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Phone (E.164)</TableHead>
                  <TableHead className="text-xs">Original</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Opted-in</TableHead>
                  <TableHead className="text-xs">Updated</TableHead>
                  <TableHead className="text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-400 text-sm">
                      No contacts yet. Import a CSV to get started.
                    </TableCell>
                  </TableRow>
                ) : contacts.rows.map(r => (
                  <TableRow key={r.e164} data-testid={`contact-row-${r.e164}`}>
                    <TableCell className="text-xs">{r.name || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.e164}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.phone_raw || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">{r.type || '—'}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.opted_in ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] border">Opted-in</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] border">Opted-out</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.updated_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        onClick={() => toggleContact(r, !r.opted_in)}
                        data-testid={`toggle-optin-${r.e164}`}
                      >
                        {r.opted_in ? 'Opt-out' : 'Opt-in'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mirror SIM → Rider preview / confirmation dialog */}
      <Dialog open={mirrorState.open} onOpenChange={(o) => !mirrorState.applying && setMirrorState((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="mirror-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b">
            <DialogTitle>Mirror SIM Inventory → Rider Mobile</DialogTitle>
            <DialogDescription>
              Overwrite each rider&apos;s personal <code>mobile_uae</code> with the number from their currently-active SIM inventory row. Idempotent — safe to run multiple times.
            </DialogDescription>
          </DialogHeader>

          {mirrorState.preview ? (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-slate-50 rounded p-2 text-center">
                    <div className="text-[10px] uppercase text-slate-500">Riders with active SIM</div>
                    <div className="text-xl font-bold" data-testid="mirror-riders-with-sim">{mirrorState.preview.riders_with_active_sim}</div>
                  </div>
                  <div className="bg-orange-50 rounded p-2 text-center">
                    <div className="text-[10px] uppercase text-orange-700">Would update</div>
                    <div className="text-xl font-bold text-orange-800" data-testid="mirror-would-update">{mirrorState.preview.would_update}</div>
                  </div>
                  <div className="bg-emerald-50 rounded p-2 text-center">
                    <div className="text-[10px] uppercase text-emerald-700">Already same</div>
                    <div className="text-xl font-bold text-emerald-800">{mirrorState.preview.already_same}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2 text-center">
                    <div className="text-[10px] uppercase text-slate-500">Rider record missing</div>
                    <div className="text-xl font-bold" data-testid="mirror-orphan-count">{mirrorState.preview.rider_not_found}</div>
                  </div>
                </div>

                <div className="border rounded overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-[11px]">Emp. ID</TableHead>
                        <TableHead className="text-[11px]">Name</TableHead>
                        <TableHead className="text-[11px]">Type</TableHead>
                        <TableHead className="text-[11px]">Current on profile</TableHead>
                        <TableHead className="text-[11px]">Will become (from SIM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mirrorState.preview.changes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500 py-8 text-xs">
                            Everything is already in sync — no updates needed.
                          </TableCell>
                        </TableRow>
                      ) : mirrorState.preview.changes.map((c) => (
                        <TableRow key={c.rider_id} data-testid={`mirror-row-${c.rider_id}`}>
                          <TableCell className="text-[11px] font-mono">{c.rider_unique_id || '—'}</TableCell>
                          <TableCell className="text-[12px]">{c.name}</TableCell>
                          <TableCell>
                            <Badge className={c.assignee_type === 'staff' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'}>
                              {c.assignee_type === 'staff' ? 'Staff' : 'Rider'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[11px] text-red-600 line-through">{c.old_mobile_uae || '(empty)'}</TableCell>
                          <TableCell className="text-[11px] text-emerald-700 font-semibold">{c.new_mobile_uae}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {mirrorState.preview.orphan_sims && mirrorState.preview.orphan_sims.length > 0 && (
                  <div className="border border-red-200 rounded overflow-hidden">
                    <div className="bg-red-50 px-3 py-2 flex items-center justify-between gap-2">
                      <div className="text-[12px] font-semibold text-red-800">
                        Rider record missing ({mirrorState.preview.orphan_sims.length}) — these SIMs point to a rider_id that doesn&apos;t exist in the Riders page
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] border-red-300 text-red-700 hover:bg-red-100 shrink-0"
                        onClick={downloadOrphansCsv}
                        data-testid="orphans-csv"
                      >
                        Download CSV
                      </Button>
                    </div>
                    <Table>
                      <TableHeader className="bg-red-50/40">
                        <TableRow>
                          <TableHead className="text-[11px]">SIM Emp. ID</TableHead>
                          <TableHead className="text-[11px]">SIM Rider Name</TableHead>
                          <TableHead className="text-[11px]">Mobile</TableHead>
                          <TableHead className="text-[11px]">Platform</TableHead>
                          <TableHead className="text-[11px]">Handover date</TableHead>
                          <TableHead className="text-[11px] font-mono">rider_id (internal)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mirrorState.preview.orphan_sims.map((o, i) => (
                          <TableRow key={o.sim_id || i} data-testid={`orphan-row-${i}`}>
                            <TableCell className="text-[11px] font-mono">{o.sim_rider_unique_id || '—'}</TableCell>
                            <TableCell className="text-[12px]">{o.sim_rider_name || <span className="text-slate-400">(no name on SIM row)</span>}</TableCell>
                            <TableCell className="text-[11px]">{o.sim_mobile}</TableCell>
                            <TableCell className="text-[11px]">{o.sim_platform || '—'}</TableCell>
                            <TableCell className="text-[11px]">{o.sim_date_of_handover || '—'}</TableCell>
                            <TableCell className="text-[10px] font-mono text-slate-500 max-w-[180px] truncate" title={o.sim_rider_id}>{o.sim_rider_id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="bg-red-50/60 px-3 py-2 text-[11px] text-red-800 leading-snug border-t border-red-200">
                      <b>Action needed:</b> for each row, either (a) open SIM Card Inventory, edit the row and re-assign the correct rider, or (b) if the rider truly left, mark the SIM as In Store. These rows are <b>skipped</b> by the mirror — they will not be updated above.
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 px-6 py-3 border-t shrink-0 bg-white">
                <Button variant="outline" onClick={() => setMirrorState({ previewing: false, applying: false, open: false, preview: null })} disabled={mirrorState.applying} data-testid="mirror-cancel">Cancel</Button>
                <Button
                  onClick={applyMirror}
                  disabled={mirrorState.applying || mirrorState.preview.would_update === 0}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="mirror-apply"
                >
                  {mirrorState.applying ? 'Applying…' : `Apply — update ${mirrorState.preview.would_update} rider${mirrorState.preview.would_update === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-500 py-8 text-center">Loading preview…</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Setup guide — renders WHATSAPP_SETUP_GUIDE.md inline so admins
          don't need repo access. */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              WhatsApp Business Cloud API — Setup Guide
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto pr-2 -mr-2 flex-1">
            {guideLoading ? (
              <div className="py-12 text-center text-slate-500">Loading…</div>
            ) : (
              <div
                className="prose prose-sm max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(guideMd) }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {logDetail && (() => {
        const raw = String(logDetail.error || '');
        // Parse Meta's inner JSON error, if any.
        let meta = null;
        try {
          const bracket = raw.indexOf('{');
          if (bracket >= 0) meta = JSON.parse(raw.slice(bracket));
        } catch { meta = null; }
        const inner = meta?.error || {};
        const code = inner.code;
        const subcode = inner.error_subcode;
        const msg = inner.message || '';
        const detail = inner.error_data?.details || '';
        // Common causes cheat-sheet.
        let diagnosis = null;
        if (raw.includes('404') && /template name does not exist|template name.*does not exist/i.test(msg + detail)) {
          diagnosis = { title: 'Template not found in this WhatsApp Business Account', fix: `Meta could not find "${logDetail.template_name}" in the WABA linked to WHATSAPP_PHONE_NUMBER_ID. Fixes: (1) confirm the template is APPROVED (green) in Meta Business Manager → WhatsApp Manager → Message Templates; (2) confirm the name matches exactly (case-sensitive, snake_case) — check the Templates tab in this app; (3) confirm the language code (usually 'en_US' or 'en') matches the template's approved language.` };
        } else if (raw.includes('404') && /phone number/i.test(msg + detail)) {
          diagnosis = { title: 'Phone Number ID mismatch', fix: 'The WHATSAPP_PHONE_NUMBER_ID env variable does not match a phone number on your Meta WABA. Fix: copy the numeric Phone Number ID from Meta Business Manager → WhatsApp Manager → Phone Numbers → your number → "API Setup" → paste into backend/.env and redeploy.' };
        } else if (code === 190 || /access token|expired|invalid/i.test(msg)) {
          diagnosis = { title: 'Access token expired or invalid', fix: 'Generate a fresh permanent System User access token in Meta Business Manager → Settings → Users → System Users. Paste into WHATSAPP_ACCESS_TOKEN in backend/.env and redeploy.' };
        } else if (code === 131047 || /re-engagement|24 hours/i.test(msg)) {
          diagnosis = { title: '24-hour customer service window closed', fix: 'This recipient last messaged you more than 24h ago. Use a pre-approved template message (not free-form text) to reach them.' };
        } else if (code === 131026 || /not a WhatsApp user|no WhatsApp account/i.test(msg)) {
          diagnosis = { title: 'Recipient is not on WhatsApp', fix: `The number ${logDetail.to_e164 || ''} is not registered on WhatsApp. Verify the rider's actual WhatsApp number — could be a data-entry mistake.` };
        } else if (code === 131056 || /pair rate|rate limit/i.test(msg)) {
          diagnosis = { title: 'Pair rate limit hit', fix: 'Too many messages to this recipient in a short window. Wait and retry.' };
        } else if (raw.includes('404')) {
          diagnosis = { title: 'Meta returned 404 — most likely template or Phone Number ID mismatch', fix: 'Cross-check: (a) template name in the Templates tab of this app matches the approved name in Meta exactly, and (b) WHATSAPP_PHONE_NUMBER_ID in backend/.env matches the phone number that owns the templates. If both look right, run "Refresh templates" on the Templates tab to re-fetch the catalog from Meta.' };
        }
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="log-detail-modal">
            <div className="bg-white rounded-lg max-w-3xl w-full shadow-2xl">
              <div className="p-4 border-b">
                <div className="text-sm font-semibold text-slate-800">
                  WhatsApp send failure — {logDetail.template_name}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {logDetail.recipient_name} · {logDetail.to_e164} · {logDetail.created_at}
                </div>
              </div>
              <div className="p-4 space-y-3">
                {diagnosis && (
                  <div className="p-3 rounded border-2 border-amber-300 bg-amber-50">
                    <div className="text-sm font-semibold text-amber-900">{diagnosis.title}</div>
                    <div className="text-[11px] text-amber-900 mt-1 leading-relaxed">{diagnosis.fix}</div>
                  </div>
                )}
                {(code || subcode) && (
                  <div className="text-[11px] flex gap-3 text-slate-600">
                    {code != null && <span>Meta code: <code className="bg-slate-100 px-1 rounded">{code}</code></span>}
                    {subcode != null && <span>Subcode: <code className="bg-slate-100 px-1 rounded">{subcode}</code></span>}
                  </div>
                )}
                <div>
                  <div className="text-[11px] font-semibold text-slate-700 mb-1">Raw error response</div>
                  <pre className="text-[10px] font-mono bg-slate-50 border rounded p-2 whitespace-pre-wrap break-all max-h-80 overflow-y-auto">{raw}</pre>
                </div>
              </div>
              <div className="p-3 border-t flex justify-end bg-slate-50">
                <Button size="sm" variant="outline" onClick={() => setLogDetail(null)} data-testid="log-detail-close">Close</Button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Message preview dialog — Eye icon on Message Log rows */}
      <Dialog open={msgPreview.open} onOpenChange={(o) => setMsgPreview((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="msg-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="w-4 h-4 text-emerald-700" />
              Message preview — {msgPreview.data?.template_name || '…'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Exactly what the recipient received / would have received. Body text mirrors the Meta-approved template.
            </DialogDescription>
          </DialogHeader>

          {msgPreview.loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading preview…
            </div>
          ) : msgPreview.data ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-50 border rounded p-3">
                <div><b className="text-slate-600">Recipient:</b>&nbsp;{msgPreview.data.recipient_name || '—'}</div>
                <div><b className="text-slate-600">Phone:</b>&nbsp;<span className="font-mono">{msgPreview.data.to_e164 || '—'}</span></div>
                <div><b className="text-slate-600">Status:</b>&nbsp;<Badge className={`${STATUS_COLORS[msgPreview.data.status] || STATUS_COLORS.pending} text-[10px] border`}>{msgPreview.data.status}</Badge></div>
                <div><b className="text-slate-600">Trigger:</b>&nbsp;<Badge variant="outline" className="text-[10px]">{msgPreview.data.trigger || '—'}</Badge></div>
                <div className="col-span-2"><b className="text-slate-600">When:</b>&nbsp;{msgPreview.data.created_at?.slice(0, 19).replace('T', ' ')}</div>
                {msgPreview.data.auto_opted_in && (
                  <div className="col-span-2 text-[11px] text-emerald-800">ℹ Recipient was auto opted-in on this send.</div>
                )}
              </div>

              {/* Rendered chat bubble — visually mimics WhatsApp */}
              <div>
                <div className="text-[11px] font-semibold text-slate-700 mb-1">Rendered body</div>
                <div className="bg-[#e5ddd5] border rounded p-3">
                  <div className="max-w-md ml-auto bg-[#dcf8c6] text-slate-900 rounded-lg rounded-tr-none px-3 py-2 shadow-sm whitespace-pre-wrap text-[13px] leading-snug">
                    {msgPreview.data.rendered_body}
                    <div className="text-[10px] text-slate-500 text-right mt-1">{msgPreview.data.created_at?.slice(11, 16) || ''}</div>
                  </div>
                </div>
              </div>

              {/* Attachment header, if any */}
              {msgPreview.data.media_header && (
                <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 flex items-center gap-2">
                  <Upload className="w-3.5 h-3.5 text-blue-700" />
                  <span><b>Attachment:</b>&nbsp;{msgPreview.data.media_header.filename || 'document'} ({msgPreview.data.media_header.type || 'document'})</span>
                </div>
              )}

              {/* Raw parameters (debug) */}
              <details className="text-[11px]">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-800">Raw template parameters ({(msgPreview.data.body_parameters || []).length})</summary>
                <table className="w-full mt-1 font-mono text-[10px] border">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-1">#</th>
                      <th className="text-left p-1">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(msgPreview.data.body_parameters || []).map((p, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1 text-slate-500">{`{{${i + 1}}}`}</td>
                        <td className="p-1">{typeof p === 'object' ? (p.text ?? JSON.stringify(p)) : String(p)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>

              {msgPreview.data.error && (
                <div className="text-[11px] bg-rose-50 border border-rose-200 rounded p-2">
                  <b className="text-rose-800">Error:</b>&nbsp;<span className="text-rose-800 break-all">{msgPreview.data.error}</span>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>


    </div>
  );
}
